import { Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import {
  getActiveEntity,
  getAllEntities,
  getEntity,
  resetActiveId,
  selectActiveEntity,
  selectAllEntities,
  selectEntityByPredicate,
  setActiveId,
  setEntities,
  updateEntitiesByPredicate,
  withActiveId,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import {
  clearRequestsCache,
  createRequestDataSource,
  createRequestsStatusOperator,
  initializeAsPending,
  updateRequestStatus,
  withRequestsCache,
  withRequestsStatus,
} from '@ngneat/elf-requests';
import {
  CheckCartResult,
  DishDto,
  DishFamilyDto,
  FluiditeTauxOccupationValues,
  GetExternalRestaurantHomeResult,
  GetSurveyResult,
  OrderSettingsDto,
  OrderSummaryDto,
  RestaurantServiceValues,
  RestaurantSousTypeValues,
  RestaurantTypeValues,
  UserSiteInfoDto,
} from '@timechef/core/api';
import { ILogger, LoggerService } from '@timechef/core/logging';
import { ensureDate } from '@timechef/core/utils';
import { capacitorStorageStrategy, includeKeys, selectAppRequestStatus } from '@timechef/core/utils/elf-utils';
import { isNullOrUndefined } from '@timechef/core/utils/js-utils';
import { isSameDay, minutesToMilliseconds } from 'date-fns';
import { groupBy, orderBy } from 'lodash';
import { Observable, map, withLatestFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { computedRestaurantOpened, isCartRetrievalEnabled } from './restaurant.utils';
import {
  CartFormula,
  CartItem,
  DishInformationEnum,
  FluiditeDetails,
  InitCartOptions,
  LAST_CLICKED_RESTAURANT_RETENTION,
  Menu,
  MenuFilterCategory,
  Restaurant,
  RestaurantServiceData,
  SelectableDish,
  SelectableDishFamily,
  SelectableFormula,
  SelectableMenu,
  SelectableSubDishFamily,
  SiteNews,
} from './restaurants.models';

interface RestaurantsProps {
  siteNews?: SiteNews;
  hiddenSiteNews: number[];
  restaurantExterne: GetExternalRestaurantHomeResult | null;
  menuFilters: {
    currentDate: Date | null;
    dates: Date[];
    closureDates: Date[];
    currentCategory: MenuFilterCategory | null;
    categories: MenuFilterCategory[];
  };
  displayOptions: {
    currentDishInformation: DishInformationEnum;
    dishInformationsAvailable: DishInformationEnum[];
  };
  scorp: {
    existingOrders: OrderSummaryDto[];
    preferredDeliveryLocationId: number | null;
    cart: {
      restaurantId: number;
      date: Date;
      pickupLimitDate?: Date;
      allowCartRetrieval: boolean;
      service: RestaurantServiceValues;
      items: CartItem[];
      formulas: CartFormula[];
      checkResult?: CheckCartResult;
    } | null;
  };
  fluidite: {
    fluiditeList: number[];
    currentFluiditeIdx: number | null;
    roundStardDate: Date | null;
  };
  lastClickedRestaurantsIds: number[];
  survey: GetSurveyResult | null;
  tableScanData: {
    restaurantId: number;
    date: Date;
    table: string;
  } | null;
}

export enum RestaurantsStatusKeys {
  Restaurants = 'restaurants',
  RestaurantExterne = 'restaurant-externe',
  RestaurantSurvey = 'restaurant-survey',
  RestaurantSurveyResponse = 'restaurant-survey-response',
  ReplyToRestaurantSurvey = 'reply-restaurant-survey',
  OrdersSummary = 'orders-summary',
  CheckCart = 'check-cart',
  CreateOrder = 'create-order',
}

@Injectable({ providedIn: 'root' })
export class RestaurantsRepository {
  constructor(loggerService: LoggerService) {
    this.logger = loggerService.getLogger('RestaurantsRepository');
  }

  private logger: ILogger;
  private store = createStore(
    { name: 'restaurants' },
    withProps<RestaurantsProps>({
      // siteNews: { id: 1, imageUrl: '../../../../assets-proxy/concept4.png', title: 'Fast & Bio !' },
      hiddenSiteNews: [],
      menuFilters: {
        currentDate: null,
        dates: [],
        closureDates: [],
        currentCategory: null,
        categories: [],
      },
      displayOptions: {
        currentDishInformation: DishInformationEnum.Calories,
        dishInformationsAvailable: [],
      },
      lastClickedRestaurantsIds: [],
      survey: null,
      restaurantExterne: null,
      fluidite: {
        fluiditeList: [],
        currentFluiditeIdx: null,
        roundStardDate: null,
      },
      scorp: {
        existingOrders: [],
        preferredDeliveryLocationId: null,
        cart: null,
      },
      tableScanData: null,
    }),
    withEntities<Restaurant>(),
    withActiveId(),
    withRequestsStatus<RestaurantsStatusKeys>(
      initializeAsPending<RestaurantsStatusKeys>([RestaurantsStatusKeys.Restaurants, RestaurantsStatusKeys.RestaurantExterne]),
    ),
    withRequestsStatus<`reply-to-restaurant-survey-${string}`>(),
    withRequestsStatus<`cancel-order.${string}`>(),
    withRequestsCache<RestaurantsStatusKeys.Restaurants>(),
  );

  instance = persistState(this.store, {
    key: 'restaurants',
    storage: capacitorStorageStrategy,
    source: () =>
      this.store.pipe(
        includeKeys([
          'entities',
          'ids',
          'activeId',
          'siteNews',
          'hiddenSiteNews',
          'menuFilters',
          'displayOptions',
          'fluidite',
          'requestsCache',
          'requestsStatus',
          'lastClickedRestaurantsIds',
          'scorp',
          'tableScanData',
        ]),
      ),
  });

  restaurantsDS = createRequestDataSource({
    store: this.store,
    data$: () =>
      this.store.pipe(
        selectAllEntities(),
        withLatestFrom(this.store.pipe(select((s) => s.lastClickedRestaurantsIds))),
        map(([restaurants, lastClickedRestaurantsIds]) => {
          return sortRestaurantsByFavoriteOrder(restaurants, lastClickedRestaurantsIds);
        }),
      ),
    requestKey: RestaurantsStatusKeys.Restaurants,
    dataKey: 'data',
    idleAsPending: true,
  });

  restaurantDetailDS = createRequestDataSource({
    store: this.store,
    data$: (restaurantId: number) =>
      this.store.pipe(
        selectAllEntities(),
        map((x) => {
          const r = x.find((r) => r.id == restaurantId)!;
          r.menus = r.menus.filter((m) => m.timedOut === false);
          return r;
        }),
      ),
    dataKey: 'data',
  });

  restaurantFluiditeDetailsDS = createRequestDataSource({
    store: this.store,
    data$: (restaurantId: string) =>
      this.store.pipe(
        selectAllEntities(),
        map((x) => x.find((r) => r.id === +restaurantId.split('.')[1])!.fluiditeDetails),
      ),
    dataKey: 'data',
  });

  restaurantSurveyCampaignDetailDS = createRequestDataSource({
    store: this.store,
    data$: (restaurantId: string) =>
      this.store.pipe(
        selectAllEntities(),
        map((x) => x.find((r) => r.id === +restaurantId.split('.')[1])!.surveyCampaignDetails),
      ),
    dataKey: 'data',
  });

  checkedCartDS = createRequestDataSource({
    store: this.store,
    data$: () =>
      this.store.pipe(
        select((s) => {
          if (!s.scorp.cart?.checkResult) return null;

          const { checkResult } = s.scorp.cart;

          return {
            preferredDeliveryLocationId: s.scorp.preferredDeliveryLocationId,
            date: s.scorp.cart.date,
            pickupLimitDate: s.scorp.cart.pickupLimitDate,
            items: s.scorp.cart.items,
            formulas: s.scorp.cart.formulas,
            service: s.scorp.cart.service,
            hasArticlesIncludedInMenu: s.scorp.cart.items.find((x) => x.isIncludedInMenu),
            hasArticlesNotIncludedInMenu: s.scorp.cart.items.find((x) => !x.isIncludedInMenu),
            ...checkResult,
          };
        }),
      ),
    requestKey: RestaurantsStatusKeys.CheckCart,
    dataKey: 'data',
    idleAsPending: true,
  });

  siteNews$ = this.store.pipe(select((s) => (!!s.siteNews && !s.hiddenSiteNews.includes(s.siteNews.id!) ? s.siteNews : undefined)));

  activeRestaurant$ = this.store.pipe(selectActiveEntity());
  isMonoRestaurant$ = this.store.pipe(
    selectAllEntities(),
    map((x) => isMonoRestaurant(x)),
  );

  hasRestaurantWithMenu$ = this.store.pipe(
    selectAllEntities(),
    map((x) => hasRestaurantWithMenu(x)),
  );

  restaurantRules$ = this.store.pipe(
    selectAllEntities(),
    map((x) => ({
      allowOrder: hasRestaurantWithOrder(x),
      allowOrderHistory: !!getFirstRestaurantWithOrderHistory(x),
      hasMenu: hasRestaurantWithMenu(x),
      hasFluidite: hasRestaurantWithFluidite(x),
      hasSurvey: hasRestaurantWithSurvey(x),
      hasAntiGaspi: hasRestaurantWithAntiGaspi(x),
    })),
  );

  currentMenuWithFilters$ = this.store.pipe(
    select((s) => {
      if (!s.menuFilters.currentDate) return null;
      if (!s.activeId) return null;

      const menu = s.entities[s.activeId]?.menus?.find((x) => isSameDay(ensureDate(x.date), ensureDate(s.menuFilters.currentDate!)));

      if (!menu) return null;

      for (const dishFamily of menu.dishFamilies) {
        for (const dish of dishFamily.dishes) {
          (dish as any).scorpCount = s.scorp.cart?.items.find((x) => x.dishId === dish.id)?.quantity ?? null;
        }
      }

      return menu;
    }),
  );

  menuFilters$ = this.store.pipe(select((s) => s.menuFilters));
  currentDishInformation$ = this.store.pipe(
    select((s) => {
      return s.displayOptions.currentDishInformation;
    }),
  );
  dishLikes$ = (restaurantId: number, dishId: number) =>
    this.store.pipe(
      selectEntityByPredicate((x) => x.id === restaurantId),
      map((x) => {
        return getFirstDishInRestaurantWithId(x!, dishId)?.likes;
      }),
    );

  restaurantExterneRequestStatus$ = this.store.pipe(selectAppRequestStatus(RestaurantsStatusKeys.RestaurantExterne));
  restaurantsRequestStatus$ = this.store.pipe(selectAppRequestStatus(RestaurantsStatusKeys.Restaurants));
  surveyResponseRequestStatus$ = this.store.pipe(selectAppRequestStatus(RestaurantsStatusKeys.RestaurantSurveyResponse));
  createOrderRequestStatus$ = this.store.pipe(selectAppRequestStatus(RestaurantsStatusKeys.CreateOrder));
  ordersSummaryRequestStatus$ = this.store.pipe(selectAppRequestStatus(RestaurantsStatusKeys.OrdersSummary));
  restaurantExterne$ = this.store.pipe(select((s) => s.restaurantExterne));
  replyToRestaurantSurveyStatus$ = this.store.pipe(selectAppRequestStatus(RestaurantsStatusKeys.ReplyToRestaurantSurvey));

  isCartFilled$ = this.store.pipe(select((s) => s.scorp.cart !== null && (s.scorp.cart.items.length > 0 || s.scorp.cart.formulas.length > 0)));
  cartRecap$ = this.store.pipe(
    select((s) => {
      const items = s.scorp.cart?.items ?? [];
      const formulas = s.scorp.cart?.formulas ?? [];
      if (!s.scorp.cart || (items.length === 0 && formulas.length === 0)) return null;
      let totalTTC = s.scorp.cart.items.reduce((prev, curr) => prev + curr.price * curr.quantity, 0);
      totalTTC = s.scorp.cart.formulas.reduce((prev, curr) => prev + curr.price * curr.quantity, totalTTC);

      const { restaurantId, date } = s.scorp.cart!;
      const forfait = items.find((x) => x.isIncludedInMenu)
        ? s.entities[restaurantId]!.menus.find((x) => isSameDay(ensureDate(x.date), ensureDate(date)))!.menuForfaitPrice
        : null;

      if (forfait) totalTTC += forfait;

      let totalItem = items.reduce((a, b) => a + b.quantity, 0);
      totalItem = formulas.reduce((a, b) => a + b.quantity, totalItem);

      return { itemCount: totalItem, totalTTC, date: s.scorp.cart.date, service: s.scorp.cart.service, allowCartRetrieval: s.scorp.cart.allowCartRetrieval };
    }),
  );

  restaurantOrdersByDate$ = (date: Date) =>
    this.store.pipe(select((s) => s.scorp.existingOrders.filter((o) => isSameDay(ensureDate(o.date), ensureDate(date)))));

  selectableDish$ = (dish: DishDto): Observable<SelectableDish | null> => {
    return this.store.pipe(
      select((s) => {
        if (!s.scorp?.cart) return null;

        const { restaurantId, date } = s.scorp.cart!;

        const restaurant = s.entities[restaurantId!];
        const { cartMaxSingleItemAmount } = restaurant.orderSettings!;
        const menu = restaurant.menus.find((x) => isSameDay(ensureDate(x.date), ensureDate(date)))!;

        const isExceptionallyClosed = restaurant.isExceptionallyClosed;

        const df = menu.dishFamilies.find((df) => df.dishes.find((d) => d.id === dish.id))!;
        const family = getDishFamilyFromDishFamilyDto(df, {
          cart: s.scorp.cart,
          cartMaxSingleItemAmount,
          restaurantType: restaurant.type,
          isExceptionallyClosed,
        });

        return getDishFromDishDto(dish, {
          cartMaxSingleItemAmount,
          cart: s.scorp.cart,
          dishes: family.dishes,
          isMaxItemsReachedFromDishFamily: family.isMaxItemsReached,
          isExceptionallyClosed,
        });
      }),
    );
  };

  selectableMenu$ = (menu: Menu): Observable<SelectableMenu> => {
    return this.store.pipe(
      select((s) => {
        const restaurant = s.entities[s.activeId!];
        const { displaySettings, type, isExceptionallyClosed } = restaurant;
        const { cartMaxSingleItemAmount } = restaurant.orderSettings!;

        // move this projection in utilities
        const dishFamilies: SelectableDishFamily[] = menu.dishFamilies.map((df) => {
          return getDishFamilyFromDishFamilyDto(df, {
            cart: s.scorp.cart,
            cartMaxSingleItemAmount,
            restaurantType: type,
            isExceptionallyClosed,
          });
        });

        const getAmountInCart = (id: number) => s.scorp.cart?.formulas.find((x: any) => x.formulaId === id)?.quantity ?? 0;

        return {
          ...menu,
          dishFamilies,
          formulas: menu.formulas?.map<SelectableFormula>((x) => ({
            ...x,
            sections: x.sections
              .map((s) => ({
                ...s,
                dishes: s.dishes.filter((x) => !x.orderLimitDate || new Date() <= ensureDate(x.orderLimitDate)),
              }))
              .filter((x) => x.dishes.length > 0),
            selectable: {
              amountInCart: getAmountInCart(x.id),
              canBeAdded: true,
              canBeRemoved: false,
              maxAllowedInCart: 1,
            },
          })),
          order: displaySettings?.categoriesOrder ?? [],
          displaySettings: {
            hideDishFamilyFilters: dishFamilies.length === 1,
            showDescription: true,
          },
        };
      }),
    );
  };

  getRestaurants = () => this.store.query(getAllEntities());
  getRestaurantExterneHome = () => this.store.query((x) => x.restaurantExterne);
  getRestaurantsIds = () => this.store.query(getAllEntities()).map((x) => x.id);
  getActiveRestaurant = () => this.store.query(getActiveEntity());
  isCartRetrivealEnabledOnActiveRestaurant = () => {
    const activeRestaurant = this.store.query(getActiveEntity());
    if (isNullOrUndefined(activeRestaurant)) return false;
    return isCartRetrievalEnabled(activeRestaurant);
  };
  getActiveRestaurantAvailableServices = () => this.store.query(getActiveEntity())?.orderSettings?.availableServices ?? [];
  getCurrentMenuFilter = () => this.store.query((x) => x.menuFilters);
  getCurrentMenuFilterDate = () => this.store.query((x) => x.menuFilters.currentDate);
  getRestaurantById = (id: number) => this.store.query(getEntity(id));
  getRestaurantsWithFluidite = () => this.store.query(getAllEntities()).filter((r) => r.hasFluidite);
  getRestaurantsWithSurveyCampaign = () => this.store.query(getAllEntities()).filter((r) => r.surveyCampaign);
  getRestaurantByAnimationId = (id: number) => this.store.query(getAllEntities()).filter((r) => r.animationId === id)[0];
  getRestaurantsAnimationsIds = () =>
    this.store
      .query(getAllEntities())
      .filter((r) => !isNullOrUndefined(r.animationId))
      .map((x) => x.animationId!);
  getRestaurantOnboardingRules = () => this.store.query(getAllEntities());

  /** If we are in a mono restaurant case, returns the restaurant's id otherwise returns null */
  getMonoRestaurantId = (): number | null => {
    const restaurants = this.getRestaurants();
    if (isMonoRestaurant(restaurants)) return restaurants[0].id;

    return null;
  };

  /** If there is only one restaurant with order, returns it, otherwise it returns null */
  getSingleRestaurantWithOrderId = (): number | null => {
    const restaurantsWithOrders = this.getRestaurants().filter((r) => r.allowOrder);
    if (restaurantsWithOrders.length === 1) return restaurantsWithOrders[0].id;

    return null;
  };

  getFirstRestaurantWithOrderHistory = () => getFirstRestaurantWithOrderHistory(this.getRestaurants());
  getFirstRestaurantWithTypeSelf = () => this.getRestaurants().find((x) => x.type === RestaurantTypeValues.Self);

  getCurrentDishInformation = () => this.store.query((s) => s.displayOptions.currentDishInformation);

  getAvailableDishInformations = () =>
    this.store.query((s) => {
      // Handle store migration
      if (!s.displayOptions.hasOwnProperty('dishInformationsAvailable')) return [DishInformationEnum.Stand];

      return s.displayOptions.dishInformationsAvailable;
    });

  getCart = () => this.store.query((s) => s.scorp.cart);
  getRestaurantOrdersByDate = (date: Date) => this.store.query((s) => s.scorp.existingOrders.filter((o) => isSameDay(ensureDate(o.date), ensureDate(date))));
  getActiveRestaurantOrderSettings = () => this.store.query((s) => s.entities[s.activeId].orderSettings);
  getRestaurantMenuByDate = (restaurantId: number, date: Date) => {
    return this.getRestaurantById(restaurantId)?.menus.find((m) => isSameDay(ensureDate(m.date), ensureDate(date)));
  };

  getRestaurantServicesByDay = (date: Date) => {
    const services: RestaurantServiceData[] = this.getActiveRestaurantAvailableServices().map((s) => ({
      service: s,
      existingOrderId: null,
    }));

    const existingOrdersOfDay = this.getRestaurantOrdersByDate(ensureDate(date));
    existingOrdersOfDay.forEach((o) => {
      const service = services.find((s) => s.service === o.service);
      if (service) {
        service.existingOrderId = o.orderId;
      }
    });

    return existingOrdersOfDay;
  };

  addLastClickedRestaurant(id: number) {
    this.store.update((s) => {
      const lastClickedRestaurantsIds = [id, ...s.lastClickedRestaurantsIds].slice(0, LAST_CLICKED_RESTAURANT_RETENTION);

      // Little hack : we clone entities (restaurants) to trigger the Datasource and refresh the list (otherwise we should subscribe to DS + an observable lastClickedRestaurants$ and merge the result in the component)
      return { ...s, lastClickedRestaurantsIds, entities: { ...s.entities } };
    });
  }

  setPreferredDeliveryLocation(deliveryLocationId: number | null) {
    this.store.update((s) => ({ ...s, scorp: { ...s.scorp, preferredDeliveryLocationId: deliveryLocationId } }));
  }

  initCart(options: InitCartOptions) {
    const { restaurantId, date, service, allowCartRetrieval, pickupLimitDate } = options;
    this.store.update((s) => ({
      ...s,
      scorp: {
        ...s.scorp,
        cart: {
          date,
          pickupLimitDate,
          allowCartRetrieval,
          restaurantId,
          service,
          formulas: [],
          items: [],
        },
      },
    }));
  }

  resetCart() {
    this.store.update((s) => ({
      ...s,
      scorp: {
        ...s.scorp,
        cart: null,
      },
    }));
  }

  addOrUpdateFormulaInCart(formula: CartFormula) {
    this.store.update((s) => {
      const cart = s.scorp.cart!;
      const existing = cart.formulas.find((x) => x.formulaId === formula.formulaId && x.tempFormulaGuid === formula.tempFormulaGuid);
      if (existing) {
        existing.dishes = formula.dishes;
      } else {
        cart.formulas.push({
          formulaId: formula.formulaId,
          tempFormulaGuid: crypto.randomUUID(),
          formulaLabel: formula.formulaLabel,
          quantity: 1,
          price: formula.price,
          dishes: formula.dishes,
        });
      }
      return { ...s, scorp: { ...s.scorp, cart } };
    });
  }

  deleteFormulaFromCart(formula: CartFormula) {
    this.store.update((s) => {
      const cart = s.scorp.cart!;
      const updatedFormulas = cart.formulas.filter((x) => !(x.formulaId === formula.formulaId && x.tempFormulaGuid === formula.tempFormulaGuid));

      return { ...s, scorp: { ...s.scorp, cart: { ...cart, formulas: updatedFormulas } } };
    });
  }

  addDishToCart(dish: DishDto) {
    this.store.update((s) => {
      const cart = s.scorp.cart!;
      const existing = cart.items.find((x) => x.dishId === dish.id);
      if (existing) {
        existing.quantity++;
      } else {
        cart.items.push({
          dishId: dish.id,
          dishLabel: dish.label,
          quantity: 1,
          price: dish.price || 0,
          isIncludedInMenu: dish.isIncludedInMenu,
        });
      }
      return { ...s, scorp: { ...s.scorp, cart } };
    });
  }

  removeDishFromCart(dishId: number, forceTotalRemove = false) {
    this.store.update((s) => {
      const cart = s.scorp.cart!;

      const existing = cart.items.find((x) => x.dishId === dishId);

      if (!existing) {
        return { ...s };
      }

      if (--existing.quantity <= 0 || forceTotalRemove) {
        cart.items = cart.items.filter((x) => x.dishId !== dishId);
      }

      return { ...s, scorp: { ...s.scorp, cart } };
    });
  }

  setCheckedCart(checkResult: CheckCartResult) {
    this.store.update(
      (s) => ({
        ...s,
        scorp: { ...s.scorp, cart: { ...s.scorp.cart!, checkResult } },
      }),
      this.checkedCartDS.setSuccess(),
    );
  }

  setCreateOrderSuccess(order: OrderSummaryDto) {
    this.resetCart();

    this.store.update(
      (s) => ({
        ...s,
        scorp: {
          ...s.scorp,
          existingOrders: [...s.scorp.existingOrders, order],
        },
      }),
      updateRequestStatus(RestaurantsStatusKeys.CreateOrder, 'success'),
    );
  }

  removeOrderFromScorp(orderId: number) {
    this.store.update((s) => ({
      ...s,
      scorp: {
        ...s.scorp,
        existingOrders: [...s.scorp.existingOrders.filter((x) => x.orderId !== orderId)],
      },
    }));
  }

  setRestaurantOrders(restaurantOrders: OrderSummaryDto[]) {
    this.store.update(
      (s) => ({
        ...s,
        scorp: {
          ...s.scorp,
          existingOrders: restaurantOrders,
        },
      }),
      updateRequestStatus(RestaurantsStatusKeys.OrdersSummary, 'success'),
    );
  }

  refreshRestaurantOrderSettings(restaurantId: number, orderSettings: OrderSettingsDto) {
    this.store.update(
      updateEntitiesByPredicate(
        (x) => x.id === restaurantId,
        (r) => ({ ...r, orderSettings }),
      ),
    );
  }

  setRestaurants(restaurants: Restaurant[]) {
    this.store.update(
      this.restaurantsDS.setCached({ ttl: minutesToMilliseconds(environment.cache.defaultMinutes) }),
      setEntities(restaurants),
      this.restaurantsDS.setSuccess(),
    );
  }

  clearRequestCache() {
    this.store.update(clearRequestsCache());
  }

  setGetSurveyChatSuccess(): void {
    this.store.update(updateRequestStatus(RestaurantsStatusKeys.RestaurantSurveyResponse, 'success'));
  }

  setRestaurantSurveyCampaignDetails(restaurantId: number, surveyCampaignDetails: GetSurveyResult) {
    this.store.update(
      updateEntitiesByPredicate(
        (x) => x.id === restaurantId,
        (r) => ({ ...r, surveyCampaignDetails }),
      ),
      this.restaurantSurveyCampaignDetailDS.setSuccess({ key: buildSurveyDsKey(restaurantId) }),
      this.restaurantSurveyCampaignDetailDS.setCached({
        key: buildSurveyDsKey(restaurantId),
        ttl: minutesToMilliseconds(environment.cache.defaultMinutes),
      }),
    );
  }

  setRestaurantCurrentFluidite(restaurantId: number, fluidite: FluiditeTauxOccupationValues) {
    const restaurant = this.getRestaurantById(restaurantId);
    if (!restaurant) {
      this.logger.warn(`setRestaurantCurrentFluidite - restaurant ${restaurantId} not found in store`);
      return;
    }

    if (restaurant.fluidite === fluidite) return;

    this.store.update(
      updateEntitiesByPredicate(
        (x) => x.id === restaurantId,
        (r) => ({ ...r, fluidite }),
      ),
    );
  }

  setRestaurantMenu(restaurantId: number, menus: Menu[]) {
    this.store.update(
      updateEntitiesByPredicate(
        (x) => x.id === restaurantId,
        (r) => ({ ...r, menus }),
      ),
      this.restaurantDetailDS.setSuccess({ key: restaurantId }),
    );
  }

  setTimeoutOnRestaurantMenu(restaurantId: number, menuDate: Date) {
    this.store.update(
      updateEntitiesByPredicate(
        (x) => x.id === restaurantId,
        (r) => ({
          ...r,
          menus: r.menus.map((m) => (isSameDay(ensureDate(m.date), ensureDate(menuDate)) ? { ...m, timedOut: true } : m)),
        }),
      ),
    );
  }

  setDishLike(restaurantId: number, dishId: number, isLiked: boolean) {
    this.store.update(
      updateEntitiesByPredicate(
        (x) => x.id === restaurantId,
        (r) => ({
          ...r,
          menus: [
            ...r.menus.map((m) => ({
              ...m,
              dishFamilies: [
                ...m.dishFamilies.map((df) => ({
                  ...df,
                  dishes: [
                    ...df.dishes.map((d) => {
                      if (d.id !== dishId) return d;

                      return {
                        ...d,
                        likes: { ...d.likes, isLikedByUser: isLiked, totalLikes: isLiked ? d.likes.totalLikes + 1 : d.likes.totalLikes - 1 },
                      };
                    }),
                  ],
                })),
              ],
            })),
          ],
        }),
      ),
    );
  }

  setIsOpen(restaurantId: number) {
    const restaurant = this.getRestaurantById(restaurantId);
    if (!restaurant) {
      this.logger.warn(`setIsOpen - restaurant ${restaurantId} not found in store`);
      return;
    }

    const isOpen = computedRestaurantOpened(restaurant);

    if (restaurant.isOpen === isOpen) return;

    this.store.update(
      updateEntitiesByPredicate(
        (x) => x.id === restaurantId,
        (r) => ({ ...r, isOpen }),
      ),
    );
  }

  setRestaurantFluiditeDetails(restaurantId: number, fluiditeDetails: FluiditeDetails) {
    this.store.update(
      updateEntitiesByPredicate(
        (x) => x.id === restaurantId,
        (r) => ({ ...r, fluiditeDetails }),
      ),
      this.restaurantDetailDS.setSuccess({ key: buildFluiditeDsKey(restaurantId) }),
    );
  }

  saveTableScanData(data: { restaurantId: number; date: Date; table: string }) {
    this.store.update((s) => ({ ...s, tableScanData: data }));
  }

  clearTableScanData() {
    this.store.update((s) => ({ ...s, tableScanData: null }));
  }

  getIsScanTableDataValid = (restaurantId: number, date: Date) =>
    this.store.query((s) => {
      if (!s.tableScanData) return false;

      return s.tableScanData.restaurantId === restaurantId && isSameDay(ensureDate(s.tableScanData.date), ensureDate(date));
    });

  setActiveRestaurantId(restaurantId: number) {
    this.store.update(setActiveId(restaurantId));
  }

  resetActiveRestaurant() {
    this.store.update(resetActiveId());
  }

  initMenuFilters(dates: Date[], closureDates: Date[]) {
    this.store.update((s) => ({ ...s, menuFilters: { ...s.menuFilters, dates, closureDates } }));
  }

  setMenuFilterCurrentDate(date: Date) {
    const restaurant = this.getActiveRestaurant();
    if (!restaurant) {
      return;
    }

    const menus = restaurant.menus;
    const menusAtDate = menus?.find((x) => isSameDay(ensureDate(x.date), ensureDate(date)));
    if (!menusAtDate) {
      return;
    }

    const categories: MenuFilterCategory[] = [];
    if (!!menusAtDate.formulas && menusAtDate.formulas.length > 0) categories.push({ label: 'Formules', ref: 'TC_INTERNAL_FORMULAS' });

    const restType = restaurant.type;
    menusAtDate.dishFamilies.forEach((df) => {
      if (restType === RestaurantTypeValues.Scorp && df.dishes.some((d) => d.composantesMenuHelios && df.externalRef === 'PP')) {
        const grouped = groupBy(
          df.dishes.filter((x) => x.composantesMenuHelios),
          (x) => x.composantesMenuHelios,
        );

        const subCategories = Object.keys(grouped).map((x) => ({
          label: x || df.label,
          ref: x ? `${df.externalRef}-${x}` : df.externalRef,
        }));

        categories.push(...orderBy(subCategories, (x) => x.label, 'asc'));
      } else {
        categories.push({ label: df.label, ref: df.externalRef });
      }
    });

    const order = restaurant.displaySettings?.categoriesOrder ?? [];

    if (restType === RestaurantTypeValues.Scorp && order.length > 0) {
      categories.sort((a, b) => {
        const aIndex = order.indexOf(a.ref);
        const bIndex = order.indexOf(b.ref);
        return aIndex - bIndex;
      });
    }

    this.store.update((s) => ({ ...s, menuFilters: { ...s.menuFilters, currentDate: date, categories, currentCategory: categories[0] } }));
  }

  setMenuFilterCurrentCategoryByIndex(index: number) {
    this.store.update((s) => ({ ...s, menuFilters: { ...s.menuFilters, currentCategory: s.menuFilters.categories[index] } }));
  }

  setMenuFilterCurrentCategory(category: MenuFilterCategory) {
    this.store.update((s) => ({ ...s, menuFilters: { ...s.menuFilters, currentCategory: category } }));
  }

  setCurrentDishInformation(currentDishInformation: DishInformationEnum) {
    this.store.update((s) => ({ ...s, displayOptions: { ...s.displayOptions, currentDishInformation } }));
  }

  setDefaultCurrentDishInformation(userSite: UserSiteInfoDto): void {
    let curr = this.store.query((s) => s.displayOptions.currentDishInformation);
    const available: DishInformationEnum[] = [];

    const setDefaultValueIfCurrentIsNotAvailable = () => (curr = DishInformationEnum.Stand);

    if (userSite.showCalories) available.push(DishInformationEnum.Calories);
    else if (curr === DishInformationEnum.Calories) setDefaultValueIfCurrentIsNotAvailable();

    if (userSite.showNutriscore) available.push(DishInformationEnum.Nutriscore);
    else if (curr === DishInformationEnum.Nutriscore) setDefaultValueIfCurrentIsNotAvailable();

    if (userSite.showCarbonscore) available.push(DishInformationEnum.Carbonscore);
    else if (curr === DishInformationEnum.Carbonscore) setDefaultValueIfCurrentIsNotAvailable();

    if (userSite.showGreenscore) available.push(DishInformationEnum.Greenscore);
    else if (curr === DishInformationEnum.Greenscore) setDefaultValueIfCurrentIsNotAvailable();

    if (userSite.showTypologies) available.push(DishInformationEnum.Veggie);
    else if (curr === DishInformationEnum.Veggie) setDefaultValueIfCurrentIsNotAvailable();

    available.push(DishInformationEnum.Stand);

    this.store.update((s) => ({ ...s, displayOptions: { currentDishInformation: curr, dishInformationsAvailable: available } }));
  }

  hideSiteNews(id: number) {
    this.store.update((s) => ({ ...s, hiddenSiteNews: [...s.hiddenSiteNews, id] }));
  }

  resetHiddenSiteNews() {
    this.store.update((s) => ({ ...s, hiddenSiteNews: [] }));
  }

  setRestaurantExterne(restaurantExterne: GetExternalRestaurantHomeResult) {
    this.store.update((s) => ({ ...s, restaurantExterne }), updateRequestStatus(RestaurantsStatusKeys.RestaurantExterne, 'success'));
  }

  replyToSurvey(restaurantId: number) {
    this.store.update(
      updateEntitiesByPredicate(
        (x) => x.id === restaurantId,
        (x) => ({
          ...x,
          surveyCampaign: { ...x.surveyCampaign!, alreadyAnsweredToday: true },
          surveyCampaignDetails: { ...x.surveyCampaignDetails!, alreadyAnsweredToday: true },
        }),
      ),
      updateRequestStatus(RestaurantsStatusKeys.ReplyToRestaurantSurvey, 'success'),
    );
  }

  resetReplyToSurveyStatus() {
    this.store.update(updateRequestStatus(RestaurantsStatusKeys.ReplyToRestaurantSurvey, 'idle'));
  }

  resetCreateOrderStatus() {
    this.store.update(updateRequestStatus(RestaurantsStatusKeys.CreateOrder, 'idle'));
  }

  setRequestStatusSuccess(key: RestaurantsStatusKeys) {
    this.store.update(updateRequestStatus(key, 'success'));
  }

  public trackRequestStatus = createRequestsStatusOperator(this.store);
}

export const buildFluiditeDsKey = (restaurantId: number) => `fluidite.${restaurantId}`;
export const buildSurveyDsKey = (restaurantId: number) => `survey.${restaurantId}`;

/** Returns true if there is only one restaurant which is not external */
export const isMonoRestaurant = (restaurants: Restaurant[]): boolean =>
  restaurants.length === 1 && restaurants[0].type !== RestaurantTypeValues.RestaurantExterne;

export function hasRestaurantWithOrder(restaurants: Restaurant[]): boolean {
  return restaurants.filter((x) => x.allowOrder).length > 0;
}

export function hasRestaurantWithMenu(restaurants: Restaurant[]): boolean {
  return restaurants.filter((x) => x.hasMenu).length > 0;
}

export function hasRestaurantWithFluidite(restaurants: Restaurant[]): boolean {
  return restaurants.filter((x) => x.hasFluidite).length > 0;
}

export function hasRestaurantWithSurvey(restaurants: Restaurant[]): boolean {
  return restaurants.filter((x) => x.surveyCampaign).length > 0;
}

export function hasRestaurantWithAntiGaspi(restaurants: Restaurant[]): boolean {
  return restaurants.filter((x) => x.sousType === RestaurantSousTypeValues.AntiGaspi).length > 0;
}

export function getFirstRestaurantWithOrderHistory(restaurants: Restaurant[]) {
  return restaurants.find((x) => x.allowOrderHistory);
}

export function getFirstDishInRestaurantWithId(r: Restaurant, dishId: number): DishDto | null {
  for (const menu of r.menus)
    for (const f of menu.dishFamilies) {
      const dish = f.dishes.find((x) => x.id === dishId);
      if (dish) return dish;
    }

  return null;
}

export function sortRestaurantsByFavoriteOrder(restaurants: Restaurant[], lastClickedRestaurantsIds: number[]) {
  return restaurants
    .map((restaurant) => ({
      restaurant,
      clicks: lastClickedRestaurantsIds.filter((rId) => restaurant.id === rId).length,
    }))
    .sort((a, b) => b.clicks - a.clicks)
    .map((r) => r.restaurant);
}

export const getDishFamilyFromDishFamilyDto = (
  df: DishFamilyDto,
  data: {
    cartMaxSingleItemAmount?: number;
    cart: any;
    restaurantType: RestaurantTypeValues;
    isExceptionallyClosed: boolean;
  },
): SelectableDishFamily => {
  const filteredDishes = df.dishes.filter((x) => !x.orderLimitDate || new Date() <= ensureDate(x.orderLimitDate));

  const getAmountInCart = (id: number) => data.cart?.items.find((x: any) => x.dishId === id)?.quantity ?? 0;

  const isMaxItemsReached = filteredDishes.reduce((a, b) => a + getAmountInCart(b.id), 0) >= (df.maxItemsAllowed ?? 99);

  const dishes = filteredDishes.map((d) => {
    return getDishFromDishDto(d, {
      cartMaxSingleItemAmount: data.cartMaxSingleItemAmount,
      cart: data.cart,
      dishes: filteredDishes,
      isMaxItemsReachedFromDishFamily: isMaxItemsReached,
      isExceptionallyClosed: data.isExceptionallyClosed,
    });
  });

  let dishesByComposantesMenus: SelectableSubDishFamily[] = [];
  // is categorized when is scorp + dishes with composante menu +
  if (data.restaurantType === RestaurantTypeValues.Scorp && df.dishes.some((d) => d.composantesMenuHelios) && df.externalRef === 'PP') {
    const grouped = groupBy(
      dishes.filter((x) => x.composantesMenuHelios),
      (x) => x.composantesMenuHelios,
    );

    dishesByComposantesMenus = orderBy(
      Object.keys(grouped).map((key) => {
        const groupedDishes = grouped[key].map((x) => x);
        return {
          label: key,
          dishes: orderBy(groupedDishes, [(d) => d.isPermanent, 'asc', (d) => d.label, 'asc']),
        };
      }),
      (x) => x.label,
      'asc',
    );
  }

  return {
    dishes: orderBy(dishes, [(d) => d.isPermanent, 'asc', (d) => d.label, 'asc']),
    label: df.label,
    externalRef: df.externalRef,
    maxItemsAllowed: df.maxItemsAllowed ?? 99,
    isMaxItemsReached,
    dishesByComposantesMenus,
  };
};

export const getDishFromDishDto = (
  dish: DishDto,
  data: {
    cartMaxSingleItemAmount?: number;
    cart: any;
    dishes: DishDto[];
    isMaxItemsReachedFromDishFamily: boolean;
    isExceptionallyClosed: boolean;
  },
): SelectableDish => {
  const getAmountInCart = (id: number) => data.cart?.items.find((x: any) => x.dishId === id)?.quantity ?? 0;
  const amountInCart = getAmountInCart(dish.id);
  const maxAllowedInCart = data.cartMaxSingleItemAmount ?? 99;

  const selectableDish: SelectableDish = {
    ...dish,
    selectable: {
      amountInCart,
      maxAllowedInCart,
      canBeAdded:
        !data.isExceptionallyClosed &&
        amountInCart < Math.min(maxAllowedInCart, dish.quantiteLeftInStock ?? maxAllowedInCart) &&
        !data.isMaxItemsReachedFromDishFamily &&
        (isNullOrUndefined(dish.quantiteLeftInStock) || dish.quantiteLeftInStock > 0),
      canBeRemoved: amountInCart > 0,
    },
  };

  return selectableDish;
};
